import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { EOverviewColumnSubType } from '../../../../entities/IDatagrid';
import { colorStack } from '../../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../../styleHelpers/fontSizes';
import { MultilineTextWithEllipsis } from '../../TextWithEllipsis/MultilineTextWithEllipsis';
import { FIXED_LAYOUT_ATTRIBUTE } from '../columnSizing';

const Wrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
    img {
        width: 50px;
        height: 50px;
        border-radius: 8px;
    }
`;

const WrapperLink = styled(Link)<{ $fullWidth?: boolean, $underlineOnHover?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    font-weight: 500;
    &:hover {
        text-decoration: none;
    };
    > div {
        max-width: 384px;
    }
    table[${FIXED_LAYOUT_ATTRIBUTE}] & {
        min-width: 0;
        > div {
            max-width: 100%;
        }
    }
    ${({ $fullWidth }) => $fullWidth && css`
        min-width: 0;
        > div {
            max-width: 100%;
        }
    `}
    ${({ $underlineOnHover }) => $underlineOnHover && css`
        &:hover > div {
            text-decoration: underline;
        }
    `}
`;

const WrapperNoLink = styled.div<{ $fullWidth?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    font-weight: 500;
    > div {
        max-width: 384px;
    }
    table[${FIXED_LAYOUT_ATTRIBUTE}] & {
        min-width: 0;
        > div {
            max-width: 100%;
        }
    }
    ${({ $fullWidth }) => $fullWidth && css`
        min-width: 0;
        > div {
            max-width: 100%;
        }
    `}
`;

const WrapperClickable = styled(WrapperNoLink)`
    cursor: pointer;
    &:hover > div {
        text-decoration: underline;
    }
`;

const Info = styled.span<{ highlight?: boolean }>`
    ${fontSizeAndHeight[13]};
    color: ${colorStack.content};
    font-weight: ${({ highlight }) => highlight ? 500 : 400};
    & + & {
        padding-inline-start: 8px;
        margin-inline-start: 8px;
        border-inline-start: 2px ${colorStack.ligthGrey} solid;
    }
`;

const Title = styled.div`
    ${fontSizeAndHeight[16]};
    color: ${colorStack.content};
`;

interface IProps {
    businessId: number;
    templateName: string;
    clusterId?: string;
    name?: string;
    organizationUrlName?: string;
    title: string;
    externalLink?: string;
    pictureUrl?: string;
    vieMode?: boolean;
    fullWidth?: boolean;
    openInNewTab?: boolean;
    onLinkClick?: () => void;
}

const OverviewType = (props: IProps) => {
    return (
        <Wrapper>
            {props.pictureUrl && <img src={props.pictureUrl} alt="" />}
            {props.vieMode ? (
                <WrapperNoLink $fullWidth={props.fullWidth}>
                    <span>
                        {props.name && <Info highlight>{props.name}</Info>}
                        {props.businessId ? <Info highlight>#{props.businessId}</Info> : ''}
                        {props.templateName && <Info>{props.templateName}</Info>}
                    </span>
                    <Title>
                        <MultilineTextWithEllipsis numberOfLines={1}>
                            {props.title}
                        </MultilineTextWithEllipsis>
                    </Title>
                </WrapperNoLink>
            ) : (props.onLinkClick && !props.externalLink) ? (
                <WrapperClickable
                    $fullWidth={props.fullWidth}
                    role="link"
                    tabIndex={0}
                    onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        props.onLinkClick();
                    }}
                    onKeyDown={(event: React.KeyboardEvent) => {
                        if (event.key === 'Enter') {
                            props.onLinkClick();
                        }
                    }}
                >
                    <span>
                        {props.name && <Info highlight>{props.name}</Info>}
                        {props.businessId ? <Info highlight>#{props.businessId}</Info> : ''}
                        {props.templateName && <Info>{props.templateName}</Info>}
                    </span>
                    <Title>
                        <MultilineTextWithEllipsis numberOfLines={1}>
                            {props.title}
                        </MultilineTextWithEllipsis>
                    </Title>
                </WrapperClickable>
            ) : (
                <WrapperLink
                    to={props.externalLink ? props.externalLink : `/orgs/${props.organizationUrlName}/cluster/${props.clusterId}/overview`}
                    $fullWidth={props.fullWidth}
                    $underlineOnHover={props.openInNewTab}
                    {...(props.openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                    <span>
                        {props.name && <Info highlight>{props.name}</Info>}
                        {props.businessId ? <Info highlight>#{props.businessId}</Info> : ''}
                        {props.templateName && <Info>{props.templateName}</Info>}
                    </span>
                    <Title>
                        <MultilineTextWithEllipsis numberOfLines={1}>
                            {props.title}
                        </MultilineTextWithEllipsis>
                    </Title>
                </WrapperLink>
            )}
        </Wrapper>
    );
};

export interface IOverviewProps {
    businessId: number;
    clusterId?: string;
    clusterName?: string;
    organizationName?: string;
    organizationUrlName?: string;
    clusterTemplateName?: string;
    organizationTemplateName?: string;
    externalLink?: string;
    pictureUrl?: string;
    subType: EOverviewColumnSubType;
    viewMode?: boolean;
    fullWidth?: boolean;
    openInNewTab?: boolean;
    onLinkClick?: () => void;
}

const OverviewOrganization = (props: IOverviewProps) => {
    return (
        <OverviewType
            vieMode={props.viewMode}
            businessId={props.businessId}
            templateName={props.organizationTemplateName}
            title={props.organizationName}
            externalLink={props.externalLink}
            pictureUrl={props.pictureUrl}
            fullWidth={props.fullWidth}
            openInNewTab={props.openInNewTab}
            onLinkClick={props.onLinkClick}
        />
    );
};

const OverviewCluster = (props: IOverviewProps) => {
    return (
        <OverviewType
            vieMode={props.viewMode}
            businessId={props.businessId}
            templateName={props.clusterTemplateName}
            clusterId={props.clusterId}
            name={props.organizationName}
            organizationUrlName={props.organizationUrlName}
            title={props.clusterName}
            externalLink={props.externalLink}
            pictureUrl={props.pictureUrl}
            fullWidth={props.fullWidth}
            openInNewTab={props.openInNewTab}
            onLinkClick={props.onLinkClick}
        />
    );
};

export const Overview = (props: IOverviewProps) => {
    return (
        <>
            {props.subType === EOverviewColumnSubType.organization && (
                <OverviewOrganization {...props} />
            )}
            {props.subType === EOverviewColumnSubType.cluster && (
                <OverviewCluster {...props} />
            )}
        </>
    );
};